import React from 'react';
import { logError } from 'utils/logger';
import { CustomError } from 'enums';

interface IState {
  error: null | Error;
  componentStack: null | string;
  hasError: boolean;
}

type ScopeObj = Record<string, string>;
type SetTag = (key: keyof ScopeObj, val: string) => any;

interface Scope {
  setTag: SetTag;
}

interface IErrorBoundary {
  beforeCapture?: (scope: Scope) => void;
  fallback?: ({
    error,
    componentStack,
  }: {
    error: Error;
    componentStack: string;
  }) => JSX.Element;
  logError?: boolean;
}

class ErrorBoundary extends React.Component<IErrorBoundary, IState> {
  constructor(props: IErrorBoundary) {
    super(props);
    this.state = { error: null, componentStack: null, hasError: false };
  }

  public componentDidCatch(error: Error, { componentStack }: React.ErrorInfo) {
    const { beforeCapture } = this.props;
    const scopeObj: ScopeObj = {
      customErrorType: CustomError.REACT_ERROR_BOUNDARY,
      componentStack,
    };
    const setTag: SetTag = (key, val) => (scopeObj[key] = val);
    const scopeNs = {
      setTag,
    };
    if (beforeCapture) {
      beforeCapture(scopeNs);
    }
    logError(error, scopeObj);

    this.setState({
      error,
      componentStack,
      hasError: true,
    });
  }

  public render() {
    const { hasError, error, componentStack } = this.state;
    const { children, fallback: Fallback } = this.props;
    if (hasError && error && !Fallback) {
      return (
        <div>
          <h1>Something went wrong</h1>
          <h2>Error</h2>
          {error.message}
          <h2>Component Stack</h2>
          {componentStack}
        </div>
      );
    }

    if (hasError && error && Fallback) {
      return <Fallback error={error} componentStack={componentStack || ''} />;
    }

    return children;
  }
}

export default ErrorBoundary;
